import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ThreeDot from "../../common/ThreeDot";
import EditButton from "../../common/EditButton";
import {
  deleteAgentRetailer,
  getAllAgentRetailers,
} from "../../../services/agent.service";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import DeleteButton from "../../common/DeleteButton";
import ViewDetailsButton from "../../common/ViewDetailsButton";
import { getAllRetailersGraphQL } from "../../../services/graphql.service";
import { debounce } from "lodash";

export default function AgentRetailer() {
  const { token } = useSelector((state) => state?.auth);
  const [retailers, setRetailers] = useState([]);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(null);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const fetchRetailers = async (page = 1) => {
    try {
      const params = { page };
      const res = await getAllAgentRetailers(token, params);
      if (res && res.status >= 200 && res.status < 300) {
        // console.log(res.data);

        setRetailers(res?.data?.results || []);
        setNext(res?.data?.next);
        setPrevious(res?.data?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.count || null);
        setTotalPages(Math.ceil((res?.data?.count || 0) / itemsPerPage));
      } else {
        Swal.fire(
          "Error",
          res?.data?.message ||
            "Failed to fetch agent-retailer. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching agent-retailer:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchRetailersGraphQL = async (page = 1, search) => {
    console.log("Fetching Retailers:", { search, page });

    try {
      const res = await getAllRetailersGraphQL(token, page, search, "agent");

      if (res && res.status >= 200 && res.status < 300) {
        console.log("Retailers fetched successfully:", res.data);

        setRetailers(res?.data?.data?.custome_users?.results);
        setNext(res?.data?.data?.custome_users?.next);
        setPrevious(res?.data?.data?.custome_users?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.data?.custome_users?.count || null);
        setTotalPages(
          Math.ceil((res?.data?.data?.custome_users?.count || 0) / itemsPerPage)
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch Retailers. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching retailers:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearchHandler(value);
  };

  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    if (debouncedSearch) {
      fetchRetailersGraphQL(currentPage, debouncedSearch);
    } else {
      fetchRetailers(currentPage);
    }
  }, [debouncedSearch, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const startEntry = (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  const handleDelete = async (retailerName, retailerID) => {
    try {
      const res = await deleteAgentRetailer(token, retailerID);
      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Deleted!",
          text: `"${retailerName}" retailer has been deleted successfully.`,
          icon: "success",
        }).then(() => {
          const remainingItems = retailers.length - 1;

          if (remainingItems === 0 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
            if (search) {
              fetchRetailersGraphQL(currentPage - 1, search);
            } else {
              fetchRetailers(currentPage - 1);
            }
          } else {
            if (search) {
              fetchRetailersGraphQL(currentPage, search);
            } else {
              fetchRetailers(currentPage);
            }
          }
        });
      } else {
        Swal.fire(
          "Error",
          res?.data?.detail ||
            "Failed to delete the retailer. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during retailer deletion:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  return (
    <div className="content-body">
      <div className="container-fluid">
        <div className="form-head d-flex mb-3 align-items-start justify-content-between">
          <div className="input-group search-area d-inline-flex">
            <input
              type="text"
              className="form-control"
              placeholder="Search here"
              onChange={(e) => handleSearchChange(e)}
            />
            <div className="input-group-append">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </div>
          <div style={{ zIndex: 2 }}>
            <Link to="/agent/agent-add-retailer">
              <button className="btn btn-primary px-3 py-2 add-btn sticky-btn">
                Add
                <img
                  className="plus-sign ms-2"
                  src="/assets/images/kidskart/icon/add.png"
                />
              </button>
            </Link>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-12 col-xxl-12 col-lg-12 col-sm-12">
            <div className="card">
              <div className="card-body p-0">
                {retailers.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table order-request2">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Shop</th>
                          <th>Retailer</th>
                          <th>Agent</th>
                          <th>Contact</th>
                          <th>Email</th>
                          <th>Orders</th>
                          <th>Sales</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {retailers.map((item, i) => (
                          <tr key={i}>
                            <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>{" "}
                            <td>{item?.shop_name}</td>
                            <td>{item?.retailer_name || item?.owner_name}</td>
                            <td>
                              {item?.added_by
                                ? item?.added_by.name
                                : item?.agent_name}
                            </td>
                            <td>{item?.mobile || item?.mobile_number}</td>
                            <td>{item?.email}</td>
                            <td>{item?.total_orders || 0}</td>
                            <td>
                              {(item?.total_sales * 1)?.toLocaleString(
                                "en-IN",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              ) || 0}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="dropdown ms-auto c-pointer">
                                  <ThreeDot />
                                  <div className="dropdown-menu dropdown-menu-end">
                                    <EditButton
                                      to={`/agent/agent-update-retailer`}
                                      id={
                                        item?.retailer_id || item?.customer_id
                                      }
                                    />
                                    <DeleteButton
                                      name={`${item?.owner_name} Retailer`}
                                      onDelete={() =>
                                        handleDelete(
                                          item?.shop_name,
                                          item?.retailer_id || item?.customer_id
                                        )
                                      }
                                    />
                                    <ViewDetailsButton
                                      to={`/agent/agent-details-retailer`}
                                      id={
                                        item?.retailer_id || item?.customer_id
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {retailers && retailers.length < 5 ? (
                          <tr style={{ height: "205px" }}></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="my-5 py-5 text-center fs-4 fw-bold">
                    Please Add Retailer
                  </div>
                )}
              </div>
            </div>

            {/* Pagination Info */}
            {totalEntries && totalEntries > 10 && (
              <div className="dataTables_wrapper no-footer">
                <div className="dataTables_info">
                  Showing {startEntry} to {endEntry} of {totalEntries} entries
                </div>
                <div className="dataTables_paginate paging_simple_numbers">
                  <button
                    className={`btn btn-primary me-2 ${
                      !previous && "disabled"
                    }`}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    {(() => {
                      const maxVisible = 7;
                      const pages = Array.from(
                        { length: totalPages },
                        (_, i) => i + 1
                      );
                      let start = Math.max(
                        currentPage - Math.floor(maxVisible / 2),
                        1
                      );
                      let end = start + maxVisible - 1;

                      if (end > totalPages) {
                        end = totalPages;
                        start = Math.max(end - maxVisible + 1, 1);
                      }

                      return pages.slice(start - 1, end).map((page) => (
                        <button
                          key={page}
                          className={`btn btn-sm mx-1 ${
                            currentPage === page
                              ? "btn-dark"
                              : "btn-outline-dark"
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                  </span>
                  <button
                    className={`btn btn-primary ms-2 ${!next && "disabled"}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
